import type { LabelField, LabelSettings } from "../types";
import {
  calculateMinimumTextWidth,
  calculateTextDimensions,
  estimateTextFit,
  ZPL_FONT_WIDTHS,
} from "./textUtils";

/**
 * Configuration for ZPL generation
 */
export interface ZPLGenerationOptions {
  includeMargins?: boolean;
  printSpeed?: number;
  darkness?: number;
}

/**
 * Result of text wrapping analysis
 */
export interface TextWrappingResult {
  needsWrapping: boolean;
  lines: string[];
  totalHeight: number;
}

/**
 * Analyze if text needs wrapping and break it into lines
 */
export function analyzeTextWrapping(
  content: string,
  fontSize: number,
  fontFamily: string,
  maxWidth: number,
  enableWrapping: boolean,
  dpi: number,
): TextWrappingResult {
  if (!content || !enableWrapping) {
    return {
      needsWrapping: false,
      lines: [content],
      totalHeight: fontSize,
    };
  }

  const minWidthNeeded = calculateMinimumTextWidth(
    content,
    fontSize,
    fontFamily,
  );
  const needsWrapping = minWidthNeeded > maxWidth;

  if (!needsWrapping) {
    return {
      needsWrapping: false,
      lines: [content],
      totalHeight: fontSize,
    };
  }

  // Break text into lines
  const textCalc = calculateTextDimensions(
    content,
    fontSize,
    fontFamily,
    maxWidth,
  );
  const lines: string[] = [];
  let remainingText = content;

  while (remainingText.length > 0) {
    const lineText = remainingText.substring(0, textCalc.charactersPerLine);
    lines.push(lineText);
    remainingText = remainingText.substring(textCalc.charactersPerLine);
  }

  return {
    needsWrapping: true,
    lines,
    totalHeight: fontSize * lines.length,
  };
}

/**
 * Calculate character width for a given font and size
 */
function getCharacterWidth(fontSize: number, fontFamily: string): number {
  const baseCharWidth = ZPL_FONT_WIDTHS[fontFamily] || ZPL_FONT_WIDTHS["0"];
  return baseCharWidth * (fontSize / 20); // Scale by fontSize (20 is base size)
}

/**
 * Truncate text to fit within the specified width
 */
function truncateTextToFit(
  content: string,
  fontSize: number,
  fontFamily: string,
  maxWidth: number,
): string {
  if (!content) return "";

  const maxChars = estimateTextFit(maxWidth, fontSize, fontFamily);

  if (content.length <= maxChars) {
    return content;
  }

  // Truncate and add ellipsis if there's room
  if (maxChars > 3) {
    return `${content.substring(0, maxChars - 3)}...`;
  }

  if (maxChars > 0) {
    return content.substring(0, maxChars);
  }

  return "";
}

/**
 * Break text into lines for multi-line rendering
 */
function breakTextIntoLines(
  content: string,
  fontSize: number,
  fontFamily: string,
  maxWidth: number,
  maxLines = 0,
): string[] {
  if (!content) return [];

  const textCalc = calculateTextDimensions(
    content,
    fontSize,
    fontFamily,
    maxWidth,
  );

  // If content fits on one line, return as single line
  if (textCalc.numberOfLines <= 1) {
    return [content];
  }

  const lines: string[] = [];
  let remainingText = content;
  const charsPerLine = textCalc.charactersPerLine;

  while (
    remainingText.length > 0 &&
    (maxLines === 0 || lines.length < maxLines)
  ) {
    let lineText = remainingText.substring(0, charsPerLine);

    // Try to break at word boundaries for better readability
    if (remainingText.length > charsPerLine) {
      const lastSpaceIndex = lineText.lastIndexOf(" ");
      if (lastSpaceIndex > charsPerLine * 0.75) {
        // Only break at word if not too far back
        lineText = lineText.substring(0, lastSpaceIndex);
        remainingText = remainingText.substring(lastSpaceIndex + 1); // Skip the space
      } else {
        remainingText = remainingText.substring(charsPerLine);
      }
    } else {
      remainingText = "";
    }

    lines.push(lineText.trim());
  }

  return lines.filter((line) => line.length > 0);
}

/**
 * Generate ZPL commands for a text field
 */
export function generateTextFieldZPL(
  field: LabelField,
  printX: number,
  printY: number,
): string {
  if (field.type !== "text") return "";

  let zpl = "";
  let content = field.content;
  const fontSize = field.fontSize || 20;
  const fontFamily = field.fontFamily || "0";
  const lineHeight = field.lineHeight || 1.2;

  // Truncate content if truncation is enabled
  if (field.truncate) {
    content = truncateTextToFit(content, fontSize, fontFamily, field.width);
  }

  // Handle multi-line text
  if (field.multiline && !field.truncate) {
    const lines = breakTextIntoLines(
      content,
      fontSize,
      fontFamily,
      field.width,
      field.maxLines || 0,
    );

    // Render each line separately
    const lineSpacing = Math.round(fontSize * lineHeight);

    for (let i = 0; i < lines.length; i++) {
      const lineY = printY + i * lineSpacing;
      const lineContent = lines[i];

      if (!lineContent.trim()) continue;

      // Calculate position based on alignment
      let finalX = printX; // Default left alignment

      if (field.alignment === "R") {
        const textWidth = calculateMinimumTextWidth(
          lineContent,
          fontSize,
          fontFamily,
        );
        finalX = printX + Math.max(0, field.width - textWidth);
      } else if (field.alignment === "C") {
        const textWidth = calculateMinimumTextWidth(
          lineContent,
          fontSize,
          fontFamily,
        );
        finalX = printX + Math.max(0, (field.width - textWidth) / 2);
      }

      // Add positioning and font commands only once per line
      zpl += `^FO${Math.round(finalX)},${lineY}\n`;
      zpl += `^A${fontFamily}N,${fontSize}\n`;
      zpl += `^FD${lineContent}^FS\n`;
    }

    return zpl;
  }

  // Single line behavior (existing logic)
  zpl += `^FO${printX},${printY}\n`;
  zpl += `^A${fontFamily}N,${fontSize}\n`;

  if (field.truncate) {
    // For truncated fields, don't use ^FB to avoid wrapping
    // Just position the text with alignment if needed
    if (field.alignment === "R") {
      // Right alignment: position at the right edge of the field
      zpl += `^FO${printX + field.width},${printY}\n`;
      zpl += `^A${fontFamily}N,${fontSize}\n`;
      zpl += `^FD${content}^FS\n`;
      return zpl;
    }

    if (field.alignment === "C") {
      // Center alignment: calculate center position
      const textWidth = calculateMinimumTextWidth(
        content,
        fontSize,
        fontFamily,
      );
      const centerX = printX + Math.max(0, (field.width - textWidth) / 2);
      zpl += `^FO${Math.round(centerX)},${printY}\n`;
      zpl += `^A${fontFamily}N,${fontSize}\n`;
      zpl += `^FD${content}^FS\n`;
      return zpl;
    }

    // Left alignment (default)
    zpl += `^FD${content}^FS\n`;
    return zpl;
  }

  // Use field block for wrapping behavior (existing logic)
  zpl += `^FB${field.width},1,0,${field.alignment || "L"}\n`;
  zpl += `^FD${content}\\&^FS\n`;

  return zpl;
}

/**
 * Generate ZPL commands for a barcode field
 */
export function generateBarcodeFieldZPL(
  field: LabelField,
  printX: number,
  printY: number,
): string {
  if (field.type !== "barcode") return "";

  // Always use Code 128 (BCN) for now
  let zpl = `^FO${printX},${printY}\n`;
  zpl += `^BY${field.barWidth || 2}\n`;
  zpl += `^BCN,${field.barcodeHeight || 50},${field.showText ? "Y" : "N"},N,Y,N\n`;
  zpl += `^FD${field.content}^FS\n`;

  return zpl;
}

/**
 * Generate ZPL for a box/border field
 */
function generateBoxFieldZPL(
  field: LabelField,
  printX: number,
  printY: number,
): string {
  if (field.type !== "box") return "";

  const borderWidth = field.borderWidth || 1;
  const borderColor = field.borderColor || "black";

  // ZPL ^GB command: ^GBw,h,t,c,r
  // w = width in dots
  // h = height in dots
  // t = border thickness in dots
  // c = line color (B = black, W = white)
  // r = rounding degree (0 = square corners)

  const colorCode = borderColor === "white" ? "W" : "B";

  let zpl = `^FO${printX},${printY}\n`;
  zpl += `^GB${field.width},${field.height},${borderWidth},${colorCode},0^FS\n`;

  return zpl;
}

/**
 * Generate complete ZPL code for a label
 */
export function generateZPL(
  settings: LabelSettings,
  fields: LabelField[],
  options: ZPLGenerationOptions = {},
): string {
  const { includeMargins = true, printSpeed = 4, darkness = 20 } = options;

  let zpl = `^XA^PR${printSpeed}\n^PW${settings.width}\n~SD${darkness}\n`;

  const marginLeft = includeMargins ? settings.marginLeft || 8 : 0;
  const marginTop = includeMargins ? settings.marginTop || 8 : 0;

  for (const field of fields) {
    const printX = field.x + marginLeft;
    const printY = field.y + marginTop;

    if (field.type === "text") {
      zpl += generateTextFieldZPL(field, printX, printY);
    } else if (field.type === "barcode") {
      zpl += generateBarcodeFieldZPL(field, printX, printY);
    } else if (field.type === "box") {
      zpl += generateBoxFieldZPL(field, printX, printY);
    }
  }

  zpl += "^XZ";
  return zpl;
}
